import ExcelJS from 'exceljs';
import type { Lancamento, LancamentoSituacao } from './financeiroShared';

export type ExportLancamentosSituacaoFiltro = 'todas' | LancamentoSituacao;
export type ExportLancamentosTipoFiltro = 'todas' | 'entrada' | 'saida';

const LIGCHAT_XLSX_THEME = {
  accentPrimary: 'FF2563EB',
  accentDeep: 'FF1D4ED8',
  onAccent: 'FFFFFFFF',
  bgCard: 'FFF8FAFC',
  bgWhite: 'FFFFFFFF',
  border: 'FFE2E8F0',
  textPrimary: 'FF0F172A',
  textMuted: 'FF64748B',
  bgSubtle: 'FFF1F5F9',
  totalRowBg: 'FFE2E8F0',
  textNeg: 'FFB91C1C',
} as const;

const FONT_FAMILY = 'Calibri';

function thinBorder(color: string): Partial<ExcelJS.Borders> {
  const edge: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: color } };
  return { top: edge, left: edge, bottom: edge, right: edge };
}

function formatDataBr(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
}

function rangeTitlePt(startIso: string, endIso: string): string {
  return `Lançamentos — ${formatDataBr(startIso)} a ${formatDataBr(endIso)}`;
}

function exportTimestampPt(): string {
  const n = new Date();
  const d = n.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const t = n.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `Exportado em ${d} às ${t}`;
}

function situacaoPlanilha(s: Lancamento['situacao']): string {
  if (s === 'pago') return 'Pago';
  if (s === 'atrasado') return 'Em atraso';
  return 'Pendente';
}

function tipoPlanilha(t: Lancamento['tipo']): string {
  return t === 'entrada' ? 'Entrada' : 'Saída';
}

/** Saídas saem com sinal negativo na planilha; entradas, positivo. */
function signedValor(l: Lancamento, campo: 'valor' | 'valorPago'): number {
  const v = Number(l[campo]) || 0;
  return l.tipo === 'saida' ? -v : v;
}

/**
 * Seleção de lançamentos para exportação, com critério adequado a cada situação:
 *
 * - **Pago**: qualquer lançamento com pagamento dentro do intervalo (foco em NF —
 *   inclui pendentes/atrasados que receberam quitação parcial no período).
 * - **Pendente**: lançamentos pendentes com vencimento ou pagamento parcial no intervalo.
 * - **Atrasado**: lançamentos em atraso com vencimento ou pagamento parcial no intervalo
 *   (continuam atrasados enquanto houver saldo, mesmo com quitação parcial recente).
 * - **Todas**: união — qualquer lançamento com pagamento ou vencimento no intervalo.
 */
export function pickLancamentosParaExport(
  lancamentos: Lancamento[],
  start: string,
  end: string,
  tipo: ExportLancamentosTipoFiltro,
  situacao: ExportLancamentosSituacaoFiltro
): Lancamento[] {
  const hasPaymentInRange = (l: Lancamento): boolean => {
    const pags = l.pagamentos ?? [];
    if (pags.length > 0) return pags.some((p) => p.data >= start && p.data <= end);
    return Boolean(l.dataPagamento && l.dataPagamento >= start && l.dataPagamento <= end);
  };

  const dueInRange = (l: Lancamento): boolean => {
    const due = l.vencimento || l.data;
    return Boolean(due && due >= start && due <= end);
  };

  let list: Lancamento[];
  if (situacao === 'pago') {
    // Pago: vê qualquer pagamento no período, independente da situação atual do lançamento.
    list = lancamentos.filter(hasPaymentInRange);
  } else if (situacao === 'pendente') {
    list = lancamentos.filter(
      (l) => l.situacao === 'pendente' && (dueInRange(l) || hasPaymentInRange(l))
    );
  } else if (situacao === 'atrasado') {
    list = lancamentos.filter(
      (l) => l.situacao === 'atrasado' && (dueInRange(l) || hasPaymentInRange(l))
    );
  } else {
    // Todas: união dos critérios.
    list = lancamentos.filter((l) => hasPaymentInRange(l) || dueInRange(l));
  }

  if (tipo !== 'todas') list = list.filter((l) => l.tipo === tipo);

  return list.sort((a, b) => {
    const ad = lastPaymentDate(a) ?? a.vencimento ?? a.data;
    const bd = lastPaymentDate(b) ?? b.vencimento ?? b.data;
    if (ad !== bd) return ad.localeCompare(bd);
    return a.id.localeCompare(b.id);
  });
}

function lastPaymentDate(l: Lancamento): string | undefined {
  const pags = l.pagamentos ?? [];
  if (pags.length === 0) return l.dataPagamento ?? undefined;
  return pags.reduce<string>((acc, p) => (p.data > acc ? p.data : acc), pags[0].data);
}

/** Método exibido no export: prioriza o pagamento; cai no método do lançamento. */
function metodoPagamentoExport(
  pagamento: { metodo?: string } | undefined,
  lancamento: Lancamento
): string {
  return pagamento?.metodo?.trim() || lancamento.metodo?.trim() || '';
}

/** Método da linha-pai: com 1 pagamento no período, usa o dele; senão, do lançamento. */
function metodoLancamentoExport(l: Lancamento, pagamentosInRange: Lancamento['pagamentos']): string {
  if (pagamentosInRange.length === 1) {
    return metodoPagamentoExport(pagamentosInRange[0], l);
  }
  return l.metodo?.trim() ?? '';
}

/**
 * Gera .xlsx estilizado (Ligchat) com lançamentos (entradas e/ou saídas) e dispara download.
 *
 * Layout das colunas varia conforme o filtro de situação:
 * - `pago` / `todas`: todas as colunas (Tipo, Descrição, Cliente, Categoria,
 *   Método de pagamento, Data, Vencimento, Data pagamento, Situação, Valor previsto, Pago).
 * - `atrasado`: omite "Data pagamento" — atrasados ainda não foram quitados.
 * - `pendente`: omite "Data" e "Data pagamento" — só interessam Vencimento,
 *   Situação e os valores.
 *
 * Significado da coluna **Pago**:
 * - `pago` / `todas`: valor efetivamente recebido/pago no período.
 * - `pendente` / `atrasado`: **saldo restante** (Previsto − Pago) — o que ainda
 *   falta, não o já quitado.
 *
 * Em filtros de pagamento (`pago`/`todas`), lançamentos com 2 ou mais quitações
 * parciais ganham linhas filhas (uma por parcela, ordenadas por data); o pai
 * vira fórmula `SUM` das filhas. Quitação integral fica só no pai. Em filtros
 * de saldo (`pendente`/`atrasado`) não há linhas filhas — o pai mostra direto o
 * valor devido. O total final soma apenas as linhas-pai, evitando dupla contagem.
 */
type ColKey =
  | 'tipo'
  | 'descricao'
  | 'cliente'
  | 'categoria'
  | 'metodo'
  | 'data'
  | 'vencimento'
  | 'dataPagamento'
  | 'situacao'
  | 'valorPrevisto'
  | 'pago';

const COLUMN_DEFS: Record<ColKey, { header: string; width: number; align: 'left' | 'center' | 'right' }> = {
  tipo: { header: 'Tipo', width: 10, align: 'center' },
  descricao: { header: 'Descrição', width: 28, align: 'left' },
  cliente: { header: 'Cliente / Fornecedor', width: 22, align: 'left' },
  categoria: { header: 'Categoria', width: 22, align: 'left' },
  metodo: { header: 'Método de pagamento', width: 18, align: 'left' },
  data: { header: 'Data', width: 12, align: 'center' },
  vencimento: { header: 'Vencimento', width: 12, align: 'center' },
  dataPagamento: { header: 'Data pagamento', width: 14, align: 'center' },
  situacao: { header: 'Situação', width: 12, align: 'center' },
  valorPrevisto: { header: 'Valor previsto (R$)', width: 16, align: 'right' },
  pago: { header: 'Pago (R$)', width: 16, align: 'right' },
};

const ALL_COLS: ColKey[] = [
  'tipo',
  'descricao',
  'cliente',
  'categoria',
  'metodo',
  'data',
  'vencimento',
  'dataPagamento',
  'situacao',
  'valorPrevisto',
  'pago',
];

function columnsForFilter(f: ExportLancamentosSituacaoFiltro): ColKey[] {
  if (f === 'pendente') return ALL_COLS.filter((c) => c !== 'data' && c !== 'dataPagamento');
  if (f === 'atrasado') return ALL_COLS.filter((c) => c !== 'dataPagamento');
  return ALL_COLS;
}

function columnLetter(n: number): string {
  let s = '';
  let x = n;
  while (x > 0) {
    const r = (x - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}

export async function downloadLancamentosExcelStyled(
  itens: Lancamento[],
  range: { start: string; end: string },
  situacaoFiltro: ExportLancamentosSituacaoFiltro = 'todas'
): Promise<void> {
  // Em filtros de saldo aberto, a coluna "Pago" do pai vira o saldo restante
  // (o que ainda falta receber/pagar). Nesses filtros não há linhas filhas —
  // o foco é o valor devido, não o histórico de quitações.
  const mostrarSaldoRestante = situacaoFiltro === 'pendente' || situacaoFiltro === 'atrasado';
  const activeCols = columnsForFilter(situacaoFiltro);
  const colNum = new Map<ColKey, number>();
  activeCols.forEach((k, i) => colNum.set(k, i + 1));
  const idx = (k: ColKey): number => colNum.get(k) ?? -1;
  const has = (k: ColKey): boolean => colNum.has(k);

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Ligchat';
  const ws = wb.addWorksheet('Lançamentos', {
    views: [{ state: 'frozen', ySplit: 3, topLeftCell: 'A4', activeCell: 'A4' }],
  });

  ws.columns = activeCols.map((k) => ({ width: COLUMN_DEFS[k].width }));

  const t = LIGCHAT_XLSX_THEME;
  const COL_LAST = columnLetter(activeCols.length);
  const COL_PREVISTO = columnLetter(idx('valorPrevisto'));
  const COL_PAGO = columnLetter(idx('pago'));

  ws.mergeCells(`A1:${COL_LAST}1`);
  const titleCell = ws.getCell('A1');
  titleCell.value = rangeTitlePt(range.start, range.end);
  titleCell.font = { name: FONT_FAMILY, size: 16, bold: true, color: { argb: t.onAccent } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: t.accentPrimary } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  titleCell.border = thinBorder(t.accentPrimary);
  ws.getRow(1).height = 36;

  ws.mergeCells(`A2:${COL_LAST}2`);
  const subCell = ws.getCell('A2');
  subCell.value = `Ligchat · Financeiro  ·  ${exportTimestampPt()}`;
  subCell.font = { name: FONT_FAMILY, size: 11, italic: true, color: { argb: t.textMuted } };
  subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: t.bgSubtle } };
  subCell.alignment = { vertical: 'middle', horizontal: 'center' };
  subCell.border = { bottom: { style: 'thin', color: { argb: t.border } } };
  ws.getRow(2).height = 22;

  const headerRow = ws.getRow(3);
  headerRow.height = 24;
  activeCols.forEach((k, i) => {
    const def = COLUMN_DEFS[k];
    const cell = headerRow.getCell(i + 1);
    cell.value = def.header;
    cell.font = { name: FONT_FAMILY, size: 11, bold: true, color: { argb: t.onAccent } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: t.accentDeep } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: def.align,
      wrapText: true,
    };
    cell.border = thinBorder(t.accentPrimary);
  });

  // Excel exibe negativos com parênteses + cor vermelha no formato abaixo.
  const moneyFmt = '"R$"#,##0.00;[Red]("R$"#,##0.00)';

  let rowIndex = 4;
  const parentRowsForTotal: number[] = [];
  itens.forEach((l, itemIdx) => {
    const zebra = itemIdx % 2 === 1;
    const bg = zebra ? t.bgCard : t.bgWhite;
    const isSaida = l.tipo === 'saida';
    const pagamentosInRange = [...(l.pagamentos ?? [])]
      .filter((p) => p.data >= range.start && p.data <= range.end)
      .sort((a, b) => a.data.localeCompare(b.data));
    // Filhas: só fazem sentido com 2+ quitações parciais reais. Quitação integral
    // (uma única) já resume tudo no pai. Em filtros de saldo (Pendente/Atrasado)
    // também não há filhas — o foco é o saldo.
    const hasChildPagamentos = !mostrarSaldoRestante && pagamentosInRange.length >= 2;
    const ultimoPagamentoEmFiltro = pagamentosInRange.length > 0
      ? pagamentosInRange[pagamentosInRange.length - 1].data
      : null;

    const row = ws.getRow(rowIndex);

    const valorPrevisto = signedValor(l, 'valor');
    const valorPagoSigned = signedValor(l, 'valorPago');
    const saldoRestante = (Number(l.valor) || 0) - (Number(l.valorPago) || 0);
    const saldoRestanteSigned = isSaida ? -saldoRestante : saldoRestante;
    const valorPagoNoFiltroSinalizado = hasChildPagamentos
      ? pagamentosInRange.reduce((acc, p) => acc + (isSaida ? -(Number(p.valor) || 0) : Number(p.valor) || 0), 0)
      : valorPagoSigned;
    const valorColunaPago = mostrarSaldoRestante
      ? saldoRestanteSigned
      : valorPagoNoFiltroSinalizado;

    const valuesByKey: Partial<Record<ColKey, ExcelJS.CellValue>> = {
      tipo: tipoPlanilha(l.tipo),
      descricao: l.descricao?.trim() ?? '',
      cliente: l.cliente?.trim() ?? '',
      categoria: l.categoria?.trim() ?? '',
      metodo: metodoLancamentoExport(l, pagamentosInRange),
      data: formatDataBr(l.data),
      vencimento: l.vencimento ? formatDataBr(l.vencimento) : '',
      situacao: situacaoPlanilha(l.situacao),
      valorPrevisto,
    };

    if (has('dataPagamento')) {
      const dataPagPaiIso = ultimoPagamentoEmFiltro
        ?? (l.dataPagamento && l.dataPagamento >= range.start && l.dataPagamento <= range.end
          ? l.dataPagamento
          : null);
      valuesByKey.dataPagamento = dataPagPaiIso ? formatDataBr(dataPagPaiIso) : '';
    }

    // "Pago" do pai: saldo restante (filtros Pendente/Atrasado), SUM das filhas
    // (com fórmula viva), ou valor pago direto quando não há filhas.
    if (mostrarSaldoRestante) {
      valuesByKey.pago = saldoRestanteSigned;
    } else if (hasChildPagamentos) {
      const childStartRow = rowIndex + 1;
      const childEndRow = rowIndex + pagamentosInRange.length;
      valuesByKey.pago = {
        formula: `SUM(${COL_PAGO}${childStartRow}:${COL_PAGO}${childEndRow})`,
        result: valorPagoNoFiltroSinalizado,
      } as ExcelJS.CellFormulaValue;
    } else {
      valuesByKey.pago = valorPagoSigned;
    }

    activeCols.forEach((k, i) => {
      const cell = row.getCell(i + 1);
      const def = COLUMN_DEFS[k];
      const v = valuesByKey[k];
      cell.value = v ?? '';
      const isMoney = k === 'valorPrevisto' || k === 'pago';
      if (isMoney) cell.numFmt = moneyFmt;
      const cellValue = k === 'valorPrevisto' ? valorPrevisto : k === 'pago' ? valorColunaPago : 0;
      const isNeg = isMoney && cellValue < 0;
      cell.font = {
        name: FONT_FAMILY,
        size: 11,
        bold: isNeg || (k === 'descricao' && hasChildPagamentos),
        color: { argb: isNeg ? t.textNeg : t.textPrimary },
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      cell.border = thinBorder(t.border);
      cell.alignment = {
        vertical: 'middle',
        horizontal: def.align,
        wrapText: true,
      };
    });

    parentRowsForTotal.push(rowIndex);
    rowIndex += 1;

    if (hasChildPagamentos) {
      const childBg = t.bgSubtle;
      const isFullyPaid = l.situacao === 'pago';
      const childTermo = isFullyPaid
        ? isSaida
          ? 'Pagamento'
          : 'Recebimento'
        : 'Parcela';
      pagamentosInRange.forEach((p, pIdx) => {
        const cRow = ws.getRow(rowIndex);
        const valorParcelaSigned = isSaida ? -Number(p.valor) || 0 : Number(p.valor) || 0;
        const childValues: Partial<Record<ColKey, ExcelJS.CellValue>> = {
          descricao: `↳ ${childTermo} ${pIdx + 1}/${pagamentosInRange.length}${p.responsavel ? ` — ${p.responsavel.trim()}` : ''}`,
          metodo: metodoPagamentoExport(p, l),
          data: formatDataBr(p.data),
          vencimento: l.vencimento ? formatDataBr(l.vencimento) : '',
          dataPagamento: formatDataBr(p.data),
          pago: valorParcelaSigned,
        };

        activeCols.forEach((k, i) => {
          const cell = cRow.getCell(i + 1);
          const def = COLUMN_DEFS[k];
          const isMoney = k === 'valorPrevisto' || k === 'pago';
          const raw = childValues[k];
          cell.value = raw !== undefined ? raw : isMoney && k === 'valorPrevisto' ? null : '';
          if (isMoney && k === 'pago') cell.numFmt = moneyFmt;
          const isNeg = k === 'pago' && valorParcelaSigned < 0;
          cell.font = {
            name: FONT_FAMILY,
            size: k === 'descricao' ? 10 : 11,
            italic: k === 'descricao',
            color: { argb: isNeg ? t.textNeg : t.textMuted },
          };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: childBg } };
          cell.border = thinBorder(t.border);
          cell.alignment = {
            vertical: 'middle',
            horizontal: def.align,
            wrapText: k === 'descricao',
            indent: k === 'descricao' ? 1 : undefined,
          };
        });

        rowIndex += 1;
      });
    }
  });

  const totalRowNum = rowIndex;

  if (itens.length > 0 && parentRowsForTotal.length > 0) {
    const labelEndLetter = columnLetter(idx('valorPrevisto') - 1);
    ws.mergeCells(`A${totalRowNum}:${labelEndLetter}${totalRowNum}`);
    const tLabel = ws.getCell(`A${totalRowNum}`);
    tLabel.value = 'Total';
    tLabel.font = { name: FONT_FAMILY, bold: true, size: 11, color: { argb: t.textPrimary } };
    tLabel.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: t.totalRowBg } };
    tLabel.alignment = { vertical: 'middle', horizontal: 'right' };
    tLabel.border = thinBorder(t.border);

    // Total final soma apenas as linhas-pai para evitar dupla contagem do "Pago"
    // (cada pai já agrega as filhas via fórmula SUM).
    const prevRefs = parentRowsForTotal.map((r) => `${COL_PREVISTO}${r}`).join(',');
    const pagoRefs = parentRowsForTotal.map((r) => `${COL_PAGO}${r}`).join(',');

    const tPrev = ws.getCell(`${COL_PREVISTO}${totalRowNum}`);
    tPrev.value = { formula: `SUM(${prevRefs})` } as ExcelJS.CellFormulaValue;
    tPrev.numFmt = moneyFmt;
    tPrev.font = { name: FONT_FAMILY, bold: true, size: 11, color: { argb: t.textPrimary } };
    tPrev.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: t.totalRowBg } };
    tPrev.alignment = { horizontal: 'right', vertical: 'middle' };
    tPrev.border = thinBorder(t.border);

    const tPago = ws.getCell(`${COL_PAGO}${totalRowNum}`);
    tPago.value = { formula: `SUM(${pagoRefs})` } as ExcelJS.CellFormulaValue;
    tPago.numFmt = moneyFmt;
    tPago.font = { name: FONT_FAMILY, bold: true, size: 11, color: { argb: t.textPrimary } };
    tPago.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: t.totalRowBg } };
    tPago.alignment = { horizontal: 'right', vertical: 'middle' };
    tPago.border = thinBorder(t.border);
  }

  const fname = `ligchat-lancamentos-${range.start}_${range.end}.xlsx`;
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
